// src/pages/purchase-requisition/components/OrderDetailDialog.jsx
import React, { useState } from 'react';
import {
    Typography, Box, Button, Dialog, DialogTitle, DialogContent, DialogActions,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
    List, ListItem, ListItemText, IconButton, Tooltip, ClickAwayListener
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import { database } from "../../../firebase";
import { ref, get } from "firebase/database";

// Helper function to format number as accounting (no decimals, with thousand separators)
const formatAccounting = (value) => {
    if (value === null || value === undefined || value === '') return '';
    const num = parseFloat(String(value).replace(/[^0-9]/g, ''));
    if (isNaN(num)) return '';
    return new Intl.NumberFormat('id-ID', {
        style: 'decimal',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(num);
};

// Helper function to format date and time
const formatDateTime = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    const options = {
        weekday: 'long',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false, // Use 24-hour format
    };
    return new Intl.DateTimeFormat('id-ID', options).format(date);
};

// Helper function to format date and time for approval history
const formatApprovalDateTime = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    const options = {
        weekday: 'long',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false, // Use 24-hour format
    };
    return new Intl.DateTimeFormat('id-ID', options).format(date);
};


// Component for the order detail dialog
function OrderDetailDialog({
    open,
    onClose,
    selectedOrder,
    flowConfigurations,
    uomList // Prop uomList diperlukan lagi di sini
}) {
    // State untuk tooltip
    const [tooltipOpen, setTooltipOpen] = useState(false);
    const [tooltipContent, setTooltipContent] = useState({
        name: '',
        department: '',
        jabatan: '',
        roles: []
    });
    const [tooltipAnchorEl, setTooltipAnchorEl] = useState(null);

    // Helper function to get UoM name from ID
    const getUomName = (uomId) => {
        // Pastikan uomList adalah array sebelum mencoba mencari
        if (!Array.isArray(uomList)) {
            return uomId; // Jika uomList bukan array, kembalikan saja ID-nya
        }
        const uom = uomList.find(item => item.id === uomId);
        return uom ? uom.nama_uom : uomId; // Return nama_uom if found, otherwise the ID
    };

    // Fungsi untuk mengambil detail pengguna untuk tooltip
    const fetchUserDetailsForTooltip = async (uid) => {
        try {
            const userSnap = await get(ref(database, `db_user/tb_user/${uid}`));
            const userData = userSnap.val();

            if (userData) {
                const departmentSnap = await get(ref(database, `db_config/db_wfms/tb_department/${userData.id_department}`));
                const departmentName = departmentSnap.exists() ? departmentSnap.val().nama_department : 'N/A';

                const jabatanSnap = await get(ref(database, `db_config/db_wfms/tb_jabatan/${userData.id_jabatan}`));
                const jabatanName = jabatanSnap.exists() ? jabatanSnap.val().nama_jabatan : 'N/A';

                let roleNames = [];
                if (userData.id_role) {
                    const roleIds = userData.id_role.split(';');
                    for (const roleId of roleIds) {
                        const roleSnap = await get(ref(database, `db_config/db_wfms/tb_role/${roleId.trim()}`));
                        if (roleSnap.exists()) {
                            roleNames.push(roleSnap.val().nama_role);
                        }
                    }
                }

                setTooltipContent({
                    name: userData.name || 'N/A',
                    department: departmentName,
                    jabatan: jabatanName,
                    roles: roleNames.length > 0 ? roleNames : ['N/A']
                });
            } else {
                setTooltipContent({
                    name: 'Pengguna tidak ditemukan',
                    department: 'N/A',
                    jabatan: 'N/A',
                    roles: ['N/A']
                });
            }
        } catch (error) {
            console.error("Error fetching user details for tooltip:", error);
            setTooltipContent({
                name: 'Error fetching data',
                department: 'N/A',
                jabatan: 'N/A',
                roles: ['N/A']
            });
        }
    };

    const handleTooltipOpen = (event, uid) => {
        setTooltipAnchorEl(event.currentTarget);
        setTooltipOpen(true);
        fetchUserDetailsForTooltip(uid);
    };

    const handleTooltipClose = () => {
        setTooltipOpen(false);
        setTooltipAnchorEl(null);
        setTooltipContent({ name: '', department: '', jabatan: '', roles: [] }); // Bersihkan konten saat ditutup
    };

    return (
        <Dialog open={open} onClose={onClose} fullScreen> {/* Changed to fullScreen */}
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">Detail Permintaan Order</Typography>
                <IconButton
                    edge="end"
                    color="inherit"
                    onClick={onClose}
                    aria-label="close"
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                {selectedOrder && (
                    <Box>
                        {/* Informasi Pengajuan */}
                        {selectedOrder.order_title && (
                            <Box sx={{ display: 'flex', alignItems: 'center'}}>
                                <Typography variant="subtitle1" sx={{ minWidth: '150px' }}><strong>Judul Order</strong></Typography>
                                <Typography variant="subtitle1" sx={{ mr: '8px' }}> : </Typography>
                                <Typography variant="subtitle1">{selectedOrder.order_title}</Typography>
                            </Box>
                        )}

                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant="subtitle1" sx={{ minWidth: '150px' }}><strong>Pengajuan</strong></Typography>
                            <Typography variant="subtitle1" sx={{ mr: '8px' }}> : </Typography>
                            <Typography variant="subtitle1">{formatDateTime(selectedOrder.created_at)}</Typography>
                        </Box>

                        {/* Informasi Diajukan Oleh */}
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant="subtitle1" sx={{ minWidth: '150px' }}><strong>Diajukan Oleh</strong></Typography>
                            <Typography variant="subtitle1" sx={{ mr: '8px' }}> : </Typography>
                            <ClickAwayListener onClickAway={handleTooltipClose}>
                                <Tooltip
                                    open={tooltipOpen && tooltipAnchorEl !== null}
                                    onClose={handleTooltipClose}
                                    disableFocusListener
                                    disableHoverListener
                                    disableTouchListener
                                    PopperProps={{
                                        anchorEl: tooltipAnchorEl,
                                        placement: 'top',
                                    }}
                                    title={
                                        <Box sx={{ p: 1 }}>
                                            <Typography variant="body2"><strong>Nama:</strong> {tooltipContent.name}</Typography>
                                            <Typography variant="body2"><strong>Departemen:</strong> {tooltipContent.department}</Typography>
                                            <Typography variant="body2"><strong>Jabatan:</strong> {tooltipContent.jabatan}</Typography>
                                            <Typography variant="body2"><strong>Role:</strong> {tooltipContent.roles.join(', ')}</Typography>
                                        </Box>
                                    }
                                >
                                    <span
                                        onMouseEnter={(e) => handleTooltipOpen(e, selectedOrder.created_by_uid)}
                                        onMouseLeave={handleTooltipClose}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <Typography variant="subtitle1" component="span">{selectedOrder.created_by_name} </Typography>
                                    </span>
                                </Tooltip>
                            </ClickAwayListener>
                        </Box>

                        {/* Informasi Status Saat Ini */}
                        <Box sx={{ display: 'flex', alignItems: 'center'}}>
                            <Typography variant="subtitle1" sx={{ minWidth: '150px' }}><strong>Status Saat Ini</strong></Typography>
                            <Typography variant="subtitle1" sx={{ mr: '8px' }}> : </Typography>
                            <Typography variant="subtitle1">{selectedOrder.status}</Typography>
                        </Box>

                        {/* Informasi Alur Persetujuan */}
                        <Box sx={{ display: 'flex', alignItems: 'center'}}>
                            <Typography variant="subtitle1" sx={{ minWidth: '150px' }}><strong>Alur Persetujuan</strong></Typography>
                            <Typography variant="subtitle1" sx={{ mr: '8px' }}> : </Typography>
                            <Typography variant="subtitle1">{flowConfigurations.find(f => f.id === selectedOrder.flow_config_id)?.flow_name || 'N/A'}</Typography>
                        </Box>


                        <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>Detail Item Material</Typography>
                        {selectedOrder.item_details && selectedOrder.item_details.length > 0 ? (
                            <TableContainer component={Paper} sx={{ mb: 2, border: '1px solid #e0e0e0' }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Kode Material</TableCell>
                                            <TableCell>Deskripsi Material</TableCell>
                                            <TableCell>QTY</TableCell>
                                            <TableCell>UoM</TableCell>
                                            <TableCell>Valuation Price</TableCell>
                                            <TableCell>Jumlah Total</TableCell>
                                            <TableCell>Cost Center</TableCell>
                                            <TableCell>Noted Kebutuhan</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {selectedOrder.item_details.map((item, idx) => (
                                            <TableRow key={item.id || idx}>
                                                <TableCell>{item.material_code}</TableCell>
                                                <TableCell>{item.material_description}</TableCell>
                                                <TableCell>{item.quantity_jul}</TableCell>
                                                {/* Menggunakan getUomName untuk menampilkan nama UoM */}
                                                <TableCell>{getUomName(item.unit_of_measure)}</TableCell>
                                                <TableCell>{formatAccounting(item.valuation_price)}</TableCell>
                                                <TableCell>{formatAccounting(item.total_amount)}</TableCell>
                                                <TableCell>{item.cost_center}</TableCell>
                                                <TableCell>{item.notes}</TableCell>



                                            </TableRow>
                                        ))}
                                    </TableBody>

<TableRow sx={{ backgroundColor: '#f5f5f5' }}>
        <TableCell sx={{ fontWeight: 'bold' }}>
            Total Biaya:
        </TableCell>
        <TableCell></TableCell>
        <TableCell></TableCell>
        <TableCell></TableCell>
        <TableCell></TableCell>
        <TableCell sx={{ fontWeight: 'bold' }}>
            {formatAccounting(selectedOrder.total_amount)}
        </TableCell>
        <TableCell></TableCell>
        <TableCell></TableCell>
    </TableRow>

                                </Table>
                            </TableContainer>
                        ) : (
                            <Typography variant="body2" color="text.secondary">Tidak ada item material untuk order ini.</Typography>
                        )}

                        <Typography variant="subtitle1" sx={{ mt: 2 }}><strong>Riwayat Persetujuan:</strong></Typography>

{selectedOrder.approval_history && selectedOrder.approval_history.length > 0 ? (
    <List>
        {selectedOrder.approval_history.map((entry, index) => (
            <ListItem key={index}>
                <ListItemText
                    primary={
                        <>
                            {entry.step_name} - {entry.decision} oleh{" "}
                            <ClickAwayListener onClickAway={handleTooltipClose}>
                                <Tooltip
                                    open={tooltipOpen && tooltipAnchorEl !== null}
                                    onClose={handleTooltipClose}
                                    disableFocusListener
                                    disableHoverListener
                                    disableTouchListener
                                    PopperProps={{
                                        anchorEl: tooltipAnchorEl,
                                        placement: 'top',
                                    }}
                                    title={
                                        <Box sx={{ p: 1 }}>
                                            <Typography variant="body2"><strong>Nama:</strong> {tooltipContent.name}</Typography>
                                            <Typography variant="body2"><strong>Departemen:</strong> {tooltipContent.department}</Typography>
                                            <Typography variant="body2"><strong>Jabatan:</strong> {tooltipContent.jabatan}</Typography>
                                            <Typography variant="body2"><strong>Role:</strong> {tooltipContent.roles.join(', ')}</Typography>
                                        </Box>
                                    }
                                >
                                    <span
                                        onMouseEnter={(e) => handleTooltipOpen(e, entry.approver_uid)}
                                        onMouseLeave={handleTooltipClose}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <Typography variant="body2" component="span">{entry.approver_name}</Typography>
                                    </span>
                                </Tooltip>
                            </ClickAwayListener>
                        </>
                    }
                    secondary={
                        <>
                            {formatApprovalDateTime(entry.timestamp)}
                            {/* TAMBAHKAN: Tampilkan catatan untuk pengajuan ulang */}
                            {entry.decision === "Rejected" && entry.notes && (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                    Catatan Penolakan: {entry.notes}
                                </Typography>
                            )}
                            {entry.decision === "Resubmitted" && entry.notes && (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                    {entry.notes}
                                </Typography>
                            )}
                        </>
                    }
                />
            </ListItem>
        ))}
    </List>
) : (
    <Typography variant="body2" color="text.secondary">Belum ada riwayat persetujuan.</Typography>
)}

                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Tutup</Button>
            </DialogActions>
        </Dialog>
    );
}

export default OrderDetailDialog;
